import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Target,
  UserCheck,
  CalendarCheck,
  Star,
  PoundSterling,
} from "lucide-react";
import Button from "../../../../components/ui/Button/Button";
import { statsData } from "../../../../data/stats";
import "./Stats.css";

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  Target: <Target size={22} strokeWidth={1.5} />,
  UserCheck: <UserCheck size={22} strokeWidth={1.5} />,
  CalendarCheck: <CalendarCheck size={22} strokeWidth={1.5} />,
  Star: <Star size={22} strokeWidth={1.5} />,
};

const Timeline = () => {
  const timelineRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".timeline__line-fill",
        { scaleY: 0 },
        {
          scaleY: 1,
          duration: 1.5,
          ease: "power2.inOut",
          transformOrigin: "top center",
          scrollTrigger: {
            trigger: timelineRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      const steps = timelineRef.current.querySelectorAll(".timeline-step");
      gsap.fromTo(
        steps,
        { opacity: 0, x: -40 },
        {
          opacity: 1,
          x: 0,
          duration: 0.7,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: timelineRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      gsap.fromTo(
        ".timeline-bottom",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          delay: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: timelineRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, timelineRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="timeline" ref={timelineRef}>
      <div className="timeline__line">
        <div className="timeline__line-fill" />
      </div>

      {statsData.funnel.map((step, index) => (
        <div key={index} className="timeline-step">
          <div className="timeline-step__node">
            <div className="timeline-step__icon">{iconMap[step.iconName]}</div>
          </div>
          <div className="timeline-step__card">
            <div className="timeline-step__number">0{index + 1}</div>
            <h4 className="timeline-step__title">{step.title}</h4>
            <p className="timeline-step__description">{step.description}</p>
          </div>
        </div>
      ))}

      <div className="timeline-bottom">
        <div className="timeline-bottom__icon">
          <PoundSterling size={28} strokeWidth={1.5} />
        </div>
        <div className="timeline-bottom__content">
          <div className="timeline-bottom__label">The Result</div>
          <div className="timeline-bottom__text">We Only Win When You Win</div>
        </div>
      </div>
    </div>
  );
};

const Stats = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context((self) => {
      gsap.from(".animate-text", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          once: true,
        },
      });

      self.selector(".stat-number").forEach((el) => {
        const targetValue = parseInt(el.getAttribute("data-target"));
        gsap.fromTo(
          el,
          { innerText: 0 },
          {
            innerText: targetValue,
            duration: 2,
            snap: { innerText: 1 },
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
            },
          },
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="stats" ref={sectionRef}>
      <div className="stats__container">
        <div className="stats__left">
          <Timeline />
        </div>

        <div className="stats__right">
          <div className="stats__badge animate-text">{statsData.badge}</div>
          <h2 className="stats__headline animate-text">{statsData.headline}</h2>
          <p className="stats__description animate-text">
            {statsData.description}
          </p>
          <p className="stats__description stats__description--last animate-text">
            {statsData.description2}
          </p>

          <div className="stats__grid">
            {statsData.stats.map((stat, index) => (
              <div key={index} className="stat-circle animate-text">
                <div className="stat-circle__number">
                  <span className="stat-number" data-target={stat.number}>
                    0
                  </span>
                  {stat.suffix}
                </div>
                <div className="stat-circle__label">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="stats__button-wrapper animate-text">
            <Button
              text={statsData.ctaButton.text}
              variant="primary"
              link={statsData.ctaButton.link}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
